import FocusTrap from 'focus-trap-react';
import React, { MouseEventHandler, PropsWithChildren, useEffect } from 'react';
import { Portal } from 'react-portal';
import { ThemeProvider, useTheme } from 'styled-components';
import colors from '../../helpers/colors';
import { updateThemeColumns } from '../../helpers/columns';
import componentProps, { ComponentProps } from '../../helpers/componentProps';
import { useCallMethodWhenEscapePressed } from '../../hooks/useCallOnEscape';
import { applyComponentTheme } from '../../theme';
import fluidGridColumns from '../../theme/fluidGridColumns';
import { GridItem, GridItemProps } from '../Grid';
import GridContainer from '../GridContainer';
import Icon from '../Icon';
import {
  CloseButton,
  CloseButtonText,
  ContentOverlay,
  ContentWrapper,
  ModalCard,
  ModalContainer,
  StyledGrid,
} from './styles';

export type ModalProps = {
  // onClose may only be omitted if there is a focusable element with the children
  onClose?: MouseEventHandler;
  onOpen?: () => void;
  gridItemProps?: GridItemProps;
  disableBoxShadow?: boolean;
} & ComponentProps;

const Modal: React.FC<PropsWithChildren<ModalProps>> = ({
  onClose,
  onOpen,
  children,
  disableBoxShadow,
  gridItemProps = { desktop: 10, tabletLandscape: 10 },
  ...props
}) => {
  const theme = useTheme();
  const resetTheme = updateThemeColumns(theme, fluidGridColumns);

  useEffect(() => {
    onOpen?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    /*  We hide the body scroll bars when the modal is opened (i.e. when mounted), so we can't scroll the whole page.  We then
        reset this when the modal is closed (i.e. when unmounted)
    */
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  });

  const closeModalId = 'closeModalButton';
  const closeModal = (): void => {
    document.getElementById(closeModalId)?.click();
  };

  useCallMethodWhenEscapePressed(closeModal);

  return (
    <Portal>
      {/* We reset the theme here, as the Modal can be rendered from inside another GridItem, 
          which affects the calculation of the widths (for example, it could set the width to >100%). 
          As the Modal will always appear over the content of a page, it makes sense to reset the 
          column widths to the fluid grid columns. 
      */}
      <ThemeProvider theme={resetTheme}>
        <FocusTrap>
          <ModalContainer>
            <GridContainer>
              <StyledGrid>
                <GridItem {...gridItemProps}>
                  <ModalCard
                    {...componentProps(props)}
                    role="dialog"
                    aria-modal="true"
                    disableBoxShadow={disableBoxShadow}>
                    {onClose && (
                      <CloseButton onClick={onClose} id={closeModalId}>
                        <CloseButtonText>Close</CloseButtonText>
                        <Icon name="close" color={colors.neutral02} size="medium" />
                      </CloseButton>
                    )}
                    <ContentWrapper tabIndex={0}>{children}</ContentWrapper>
                  </ModalCard>
                  <ContentOverlay onClick={onClose} />
                </GridItem>
              </StyledGrid>
            </GridContainer>
          </ModalContainer>
        </FocusTrap>
      </ThemeProvider>
    </Portal>
  );
};
export default applyComponentTheme(Modal, 'Modal');
